import { useTranslation } from 'react-i18next';
import { CheckCircle2, Circle, Loader2 } from 'lucide-react';

import type { ProductionStage } from '@/hooks/useProduction';
import { cn } from '@/lib/utils';

function StageIcon({ status, live }: { status: ProductionStage['status']; live: boolean }) {
  if (status === 'complete') return <CheckCircle2 className="h-4 w-4 text-primary" />;
  if (status === 'active') {
    // Spin only while the run is actually alive — a cancelled/failed production
    // showing an animated spinner reads as "still working", which is a lie.
    return live ? (
      <Loader2 className="h-4 w-4 animate-spin text-primary" />
    ) : (
      <Circle className="h-4 w-4 fill-primary text-primary" />
    );
  }
  return <Circle className="h-4 w-4 text-track" />;
}

/**
 * The four-beat Pre-Production → Production → Post → Delivery ladder.
 *
 * `stage.name` is the English label seeded client-side in INITIAL_STAGES, so it is
 * translated here by position; an unexpected stage falls back to its own name.
 */
const STAGE_KEYS = ['pre-production', 'production', 'post-production', 'delivery'];

export default function StageTracker({ stages, live }: { stages: ProductionStage[]; live: boolean }) {
  const { t } = useTranslation('common');
  return (
    <div className="flex flex-wrap items-center">
      {stages.map((stage, i) => (
        <div key={stage.stage} className="flex items-center">
          <div className="flex items-center gap-2">
            <StageIcon status={stage.status} live={live} />
            <span
              className={cn(
                'whitespace-nowrap text-[13px]',
                stage.status === 'active' && 'font-semibold text-primary',
                stage.status === 'complete' && 'text-foreground',
                stage.status === 'pending' && 'text-faint',
              )}
            >
              {t(`stage.${STAGE_KEYS[stage.stage - 1] ?? ''}`, { defaultValue: stage.name })}
            </span>
          </div>
          {i < stages.length - 1 && <div className="mx-3 h-px w-8 bg-track" />}
        </div>
      ))}
    </div>
  );
}
